// src/services/BackupScheduler.js
import backupService from './BackupService';
import { BACKUP_CONFIG } from './backupConfig';
import { 
  formatRelativeTime, 
  formatFileSize,
  calculateBackupStats 
} from './backupUtils';

/**
 * Service de planification des sauvegardes automatiques
 * Gère les tâches périodiques et les notifications
 */
class BackupScheduler {
  constructor() {
    this.intervals = new Map();
    this.isRunning = false;
    this.lastBackupResults = new Map();
    this.failureCount = 0;
    this.maxFailures = 3;
    this.notificationCallbacks = new Set();
    
    // Statistiques du scheduler
    this.stats = {
      startTime: null,
      totalBackups: 0,
      successfulBackups: 0,
      failedBackups: 0,
      lastError: null
    };
    
    console.log('🔄 BackupScheduler initialisé');
  }

  /**
   * Démarrer le scheduler automatique
   */
  start() {
    if (this.isRunning) {
      console.log('⚠️ Scheduler déjà en cours d\'exécution');
      return;
    }

    console.log('🚀 Démarrage du BackupScheduler automatique');
    this.isRunning = true;
    this.stats.startTime = Date.now();
    this.failureCount = 0;

    // Programmer les différents types de sauvegardes
    this.scheduleAutoBackups();
    this.scheduleCleanupTasks();
    this.scheduleHealthChecks();
    
    // Notification de démarrage
    this.notifySubscribers({
      type: 'info',
      title: 'Scheduler de sauvegarde démarré',
      message: 'Les sauvegardes automatiques sont maintenant actives',
      timestamp: Date.now()
    });
  }

  /**
   * Arrêter le scheduler
   */
  stop() {
    if (!this.isRunning) {
      console.log('⚠️ Scheduler déjà arrêté');
      return;
    }

    console.log('🛑 Arrêt du BackupScheduler');
    this.isRunning = false;

    // Arrêter tous les intervalles
    this.intervals.forEach((intervalId, name) => {
      clearInterval(intervalId);
      console.log(`🔄 Intervalle "${name}" arrêté`);
    });
    
    this.intervals.clear();

    // Notification d'arrêt
    this.notifySubscribers({
      type: 'warning',
      title: 'Scheduler de sauvegarde arrêté',
      message: 'Les sauvegardes automatiques ont été désactivées',
      timestamp: Date.now()
    });
  }

  /**
   * Programmer les sauvegardes automatiques
   */
  scheduleAutoBackups() {
    const environment = this.getEnvironment();
    const config = BACKUP_CONFIG.SCHEDULING[environment];

    console.log(`📅 Programmation des sauvegardes automatiques (${environment})`);
    console.log(`⏰ Intervalle: ${config.interval / 1000}s`);

    // Sauvegarde automatique principale
    const autoBackupInterval = setInterval(async () => {
      await this.executeAutoBackup();
    }, config.interval);

    this.intervals.set('auto-backup', autoBackupInterval);

    // Sauvegarde quotidienne (à 2h du matin)
    this.scheduleDailyBackup();

    // Sauvegarde hebdomadaire (dimanche à 1h du matin)
    this.scheduleWeeklyBackup();
  }

  /**
   * Exécuter une sauvegarde automatique
   */
  async executeAutoBackup(type = 'auto') {
    if (!this.isRunning) return;

    const startTime = Date.now();
    console.log(`🔄 Démarrage sauvegarde automatique (${type})`);

    try {
      // Vérifier l'espace disque avant la sauvegarde
      const spaceCheck = await this.checkDiskSpace();
      if (!spaceCheck.hasSpace) {
        throw new Error(`Espace disque insuffisant: ${spaceCheck.available} disponible`);
      }

      // Créer la sauvegarde
      const result = await backupService.createFullBackup({
        userId: 'system',
        type: type,
        automated: true,
        scheduledAt: startTime
      });

      // Enregistrer le résultat
      this.lastBackupResults.set(type, {
        ...result,
        success: true,
        duration: Date.now() - startTime,
        timestamp: startTime
      });

      // Mettre à jour les statistiques
      this.stats.totalBackups++;
      this.stats.successfulBackups++;
      this.failureCount = 0; // Reset sur succès

      console.log(`✅ Sauvegarde automatique réussie (${type})`);
      console.log(`📊 Durée: ${Date.now() - startTime}ms`);
      console.log(`💾 Taille: ${formatFileSize(result.metadata?.totalSize || 0)}`);

      // Notification de succès
      this.notifySubscribers({
        type: 'success',
        title: `Sauvegarde ${type} réussie`,
        message: `Sauvegarde terminée en ${Math.round((Date.now() - startTime) / 1000)}s`,
        details: {
          size: formatFileSize(result.metadata?.totalSize || 0),
          documents: result.metadata?.totalDocuments || 0,
          collections: result.metadata?.collectionsCount || 0
        },
        timestamp: Date.now()
      });

      // Nettoyer les anciennes sauvegardes si nécessaire
      await this.cleanupOldBackups();

    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde automatique:', error);
      
      // Enregistrer l'échec
      this.lastBackupResults.set(type, {
        success: false,
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: startTime
      });

      // Mettre à jour les statistiques
      this.stats.totalBackups++;
      this.stats.failedBackups++;
      this.stats.lastError = error.message;
      this.failureCount++;

      // Notification d'erreur
      this.notifySubscribers({
        type: 'error',
        title: `Échec sauvegarde ${type}`,
        message: error.message,
        details: {
          attempt: this.failureCount,
          maxAttempts: this.maxFailures
        },
        timestamp: Date.now()
      });

      // Arrêter le scheduler après trop d'échecs consécutifs
      if (this.failureCount >= this.maxFailures) {
        console.error(`🚨 Trop d'échecs consécutifs (${this.failureCount}), arrêt du scheduler`);
        this.stop();
        
        this.notifySubscribers({
          type: 'error',
          title: 'Scheduler arrêté - Échecs répétés',
          message: `Le scheduler a été arrêté après ${this.failureCount} échecs consécutifs`,
          timestamp: Date.now()
        });
      }
    }
  }

  /**
   * Programmer la sauvegarde quotidienne
   */
  scheduleDailyBackup() {
    const now = new Date();
    const nextRun = new Date();
    nextRun.setHours(2, 0, 0, 0); // 2h du matin
    
    // Si on a dépassé 2h aujourd'hui, programmer pour demain
    if (nextRun <= now) {
      nextRun.setDate(nextRun.getDate() + 1);
    }

    const msUntilNextRun = nextRun.getTime() - now.getTime();
    
    console.log(`📅 Sauvegarde quotidienne programmée pour: ${nextRun.toLocaleString()}`);

    setTimeout(() => {
      // Première exécution
      this.executeAutoBackup('daily');
      
      // Puis toutes les 24h
      const dailyInterval = setInterval(() => {
        this.executeAutoBackup('daily');
      }, 24 * 60 * 60 * 1000);
      
      this.intervals.set('daily-backup', dailyInterval);
    }, msUntilNextRun);
  }

  /**
   * Programmer la sauvegarde hebdomadaire
   */
  scheduleWeeklyBackup() {
    const now = new Date();
    const nextSunday = new Date();
    
    // Calculer le prochain dimanche à 1h du matin
    const daysUntilSunday = (7 - now.getDay()) % 7;
    nextSunday.setDate(now.getDate() + daysUntilSunday);
    nextSunday.setHours(1, 0, 0, 0);
    
    // Si c'est dimanche et qu'on a dépassé 1h, programmer pour dimanche prochain
    if (now.getDay() === 0 && now.getHours() >= 1) {
      nextSunday.setDate(nextSunday.getDate() + 7);
    }

    const msUntilNextRun = nextSunday.getTime() - now.getTime();
    
    console.log(`📅 Sauvegarde hebdomadaire programmée pour: ${nextSunday.toLocaleString()}`);

    setTimeout(() => {
      // Première exécution
      this.executeAutoBackup('weekly');
      
      // Puis toutes les semaines
      const weeklyInterval = setInterval(() => {
        this.executeAutoBackup('weekly');
      }, 7 * 24 * 60 * 60 * 1000);
      
      this.intervals.set('weekly-backup', weeklyInterval);
    }, msUntilNextRun);
  }

  /**
   * Programmer les tâches de nettoyage
   */
  scheduleCleanupTasks() {
    console.log('🧹 Programmation des tâches de nettoyage');

    // Nettoyage quotidien à 3h du matin
    const cleanupInterval = setInterval(() => {
      if (new Date().getHours() === 3) {
        this.performCleanupTasks();
      }
    }, 60 * 60 * 1000); // Vérifier toutes les heures

    this.intervals.set('cleanup-tasks', cleanupInterval);
  }

  /**
   * Exécuter les tâches de nettoyage
   */
  async performCleanupTasks() {
    console.log('🧹 Démarrage des tâches de nettoyage');

    try {
      // Nettoyer les anciennes sauvegardes
      const cleanupResult = await this.cleanupOldBackups();
      
      // Optimiser la base de données (si nécessaire)
      // await this.optimizeDatabase();
      
      // Vérifier l'intégrité des sauvegardes récentes
      await this.verifyRecentBackups();

      this.notifySubscribers({
        type: 'info',
        title: 'Nettoyage terminé',
        message: `${cleanupResult.deletedCount} anciennes sauvegardes supprimées`,
        timestamp: Date.now()
      });

    } catch (error) {
      console.error('❌ Erreur lors du nettoyage:', error);
      
      this.notifySubscribers({
        type: 'warning',
        title: 'Erreur de nettoyage',
        message: error.message,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Nettoyer les anciennes sauvegardes selon la politique de rétention
   */
  async cleanupOldBackups() {
    console.log('🗑️ Nettoyage des anciennes sauvegardes...');

    try {
      const backups = await backupService.listBackups();
      const retentionPolicy = BACKUP_CONFIG.RETENTION_POLICY;
      const now = Date.now();
      let deletedCount = 0;

      // Grouper les sauvegardes par type
      const backupsByType = {
        hourly: [],
        daily: [],
        weekly: [],
        monthly: [],
        yearly: []
      };

      backups.forEach(backup => {
        const age = now - backup.timestamp;
        const days = age / (24 * 60 * 60 * 1000);

        if (days <= 2) {
          backupsByType.hourly.push(backup);
        } else if (days <= 30) {
          backupsByType.daily.push(backup);
        } else if (days <= 90) {
          backupsByType.weekly.push(backup);
        } else if (days <= 365) {
          backupsByType.monthly.push(backup);
        } else {
          backupsByType.yearly.push(backup);
        }
      });

      // Appliquer la politique de rétention pour chaque type
      for (const [type, typeBackups] of Object.entries(backupsByType)) {
        const maxCount = retentionPolicy[type.toUpperCase()];
        if (typeBackups.length > maxCount) {
          // Trier par date (plus ancien en premier)
          typeBackups.sort((a, b) => a.timestamp - b.timestamp);
          
          // Supprimer les plus anciens
          const toDelete = typeBackups.slice(0, typeBackups.length - maxCount);
          
          for (const backup of toDelete) {
            try {
              await backupService.deleteBackup(backup.id);
              deletedCount++;
              console.log(`🗑️ Sauvegarde ${backup.id} supprimée (${type})`);
            } catch (deleteError) {
              console.error(`❌ Erreur suppression ${backup.id}:`, deleteError);
            }
          }
        }
      }

      console.log(`✅ Nettoyage terminé: ${deletedCount} sauvegardes supprimées`);
      return { deletedCount };

    } catch (error) {
      console.error('❌ Erreur lors du nettoyage:', error);
      throw error;
    }
  }

  /**
   * Programmer les vérifications de santé
   */
  scheduleHealthChecks() {
    console.log('🔍 Programmation des vérifications de santé');

    // Vérification toutes les 5 minutes
    const healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, 5 * 60 * 1000);

    this.intervals.set('health-check', healthCheckInterval);
  }

  /**
   * Effectuer une vérification de santé
   */
  async performHealthCheck() {
    try {
      // Vérifier l'espace disque
      const diskSpace = await this.checkDiskSpace();
      
      // Vérifier la connectivité à la base de données
      const dbHealth = await this.checkDatabaseHealth();
      
      // Vérifier les services de stockage
      const storageHealth = await this.checkStorageHealth();

      // Alerter si des problèmes sont détectés
      if (!diskSpace.hasSpace) {
        this.notifySubscribers({
          type: 'warning',
          title: 'Espace disque faible',
          message: `Espace disponible: ${diskSpace.available}`,
          timestamp: Date.now()
        });
      }

      if (!dbHealth.isHealthy) {
        this.notifySubscribers({
          type: 'error',
          title: 'Problème base de données',
          message: dbHealth.error,
          timestamp: Date.now()
        });
      }

      if (!storageHealth.isHealthy) {
        this.notifySubscribers({
          type: 'error',
          title: 'Problème stockage',
          message: storageHealth.error,
          timestamp: Date.now()
        });
      }

    } catch (error) {
      console.error('❌ Erreur vérification santé:', error);
    }
  }

  /**
   * Vérifier l'espace disque disponible
   */
  async checkDiskSpace() {
    // Simulation - Dans un vrai environnement, utiliser des APIs système
    const totalSpace = 100 * 1024 * 1024 * 1024; // 100GB simulé
    const usedSpace = Math.random() * totalSpace;
    const availableSpace = totalSpace - usedSpace;
    const usagePercentage = (usedSpace / totalSpace) * 100;

    return {
      hasSpace: usagePercentage < 90, // Alerte si plus de 90% utilisé
      total: formatFileSize(totalSpace),
      used: formatFileSize(usedSpace),
      available: formatFileSize(availableSpace),
      usagePercentage: Math.round(usagePercentage)
    };
  }

  /**
   * Vérifier la santé de la base de données
   */
  async checkDatabaseHealth() {
    try {
      // Test simple de connectivité
      await backupService.listBackups({ limit: 1 });
      return { isHealthy: true };
    } catch (error) {
      return { 
        isHealthy: false, 
        error: `Connectivité DB: ${error.message}` 
      };
    }
  }

  /**
   * Vérifier la santé du stockage
   */
  async checkStorageHealth() {
    try {
      // Test d'écriture/lecture simple
      const testData = { test: true, timestamp: Date.now() };
      // Ici on ferait un test réel sur le stockage
      return { isHealthy: true };
    } catch (error) {
      return { 
        isHealthy: false, 
        error: `Stockage: ${error.message}` 
      };
    }
  }

  /**
   * Vérifier l'intégrité des sauvegardes récentes
   */
  async verifyRecentBackups() {
    console.log('🔍 Vérification intégrité des sauvegardes récentes');

    try {
      const backups = await backupService.listBackups({ limit: 5 });
      let verifiedCount = 0;
      let errorCount = 0;

      for (const backup of backups) {
        try {
          // Vérification basique de l'intégrité
          if (backup.metadata && backup.collections) {
            verifiedCount++;
          } else {
            errorCount++;
            console.warn(`⚠️ Sauvegarde ${backup.id} potentiellement corrompue`);
          }
        } catch (error) {
          errorCount++;
          console.error(`❌ Erreur vérification ${backup.id}:`, error);
        }
      }

      console.log(`✅ Vérification terminée: ${verifiedCount} OK, ${errorCount} erreurs`);

      if (errorCount > 0) {
        this.notifySubscribers({
          type: 'warning',
          title: 'Problèmes d\'intégrité détectés',
          message: `${errorCount} sauvegarde(s) présente(nt) des problèmes`,
          timestamp: Date.now()
        });
      }

    } catch (error) {
      console.error('❌ Erreur vérification intégrité:', error);
    }
  }

  /**
   * Obtenir l'environnement d'exécution
   */
  getEnvironment() {
    // En production, utiliser des variables d'environnement
    if (typeof window !== 'undefined') {
      return window.location.hostname === 'localhost' ? 'DEVELOPMENT' : 'PRODUCTION';
    }
    return process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'DEVELOPMENT';
  }

  /**
   * S'abonner aux notifications du scheduler
   */
  subscribe(callback) {
    this.notificationCallbacks.add(callback);
    
    return () => {
      this.notificationCallbacks.delete(callback);
    };
  }

  /**
   * Notifier tous les abonnés
   */
  notifySubscribers(notification) {
    this.notificationCallbacks.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('❌ Erreur notification callback:', error);
      }
    });
  }

  /**
   * Obtenir les statistiques du scheduler
   */
  getStats() {
    const uptime = this.stats.startTime ? Date.now() - this.stats.startTime : 0;
    const successRate = this.stats.totalBackups > 0 
      ? Math.round((this.stats.successfulBackups / this.stats.totalBackups) * 100)
      : 0;

    return {
      ...this.stats,
      uptime: formatRelativeTime(this.stats.startTime),
      uptimeMs: uptime,
      successRate,
      isRunning: this.isRunning,
      activeIntervals: Array.from(this.intervals.keys()),
      lastResults: Object.fromEntries(this.lastBackupResults)
    };
  }

  /**
   * Forcer une sauvegarde manuelle
   */
  async triggerManualBackup(type = 'manual') {
    console.log(`🔧 Déclenchement sauvegarde manuelle (${type})`);
    return await this.executeAutoBackup(type);
  }

  /**
   * Redémarrer le scheduler
   */
  restart() {
    console.log('🔄 Redémarrage du scheduler');
    this.stop();
    setTimeout(() => {
      this.start();
    }, 1000);
  }
}

// Instance singleton
const backupScheduler = new BackupScheduler();

export default backupScheduler;